/* eslint-disable max-len */
import React, { useState, useEffect, useRef } from 'react';

import axios from 'axios';
import qs from 'qs';
import { Row, Col } from 'reactstrap';

import { clinicFormSubmissionEvent } from '../../helpers/analytics';
import useI18n from '../../hooks/use-i18n';
import { countryData } from '../../language.config';
import { getCountryCodeByIsd, validateEmail } from '../../utils/utils';

import '../../styles/dentistForm.scss';

function DentistForm() {
  const [isOpen, setIsOpen] = useState(false);
  const i18n = useI18n();
  const pageData = i18n.t('dentist_page.dentist_form');
  const loginData = i18n.t('login_page');
  const codes = i18n.activeLocale.split('-');
  const country = codes.length === 2 ? codes[0] : 'sg';
  const dropdown = useRef();

  useEffect(() => {
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  const handleClick = (e) => {
    if (dropdown.current && dropdown.current.contains(e.target)) {
      return;
    }
    setIsOpen(false);
  };

  const defaultFormData = {
    partner_type: '',
    full_name: '',
    ...country === 'jp' && {
      full_name_kana: ''
    },
    email: '',
    timestamp: new Date().toLocaleString(),
    name_dental_practice: '',
    country_code: country,
    dial_code: countryData[country].dialCode,
    phone_number: '',
    years_dental_experience: '',
    clear_aligner_cases_completed: '',
    clinic_capacity: '',
    street_address: '',
    city: '',
    country: countryData[country].name
  };
  const defaultFormErrors = {
    partner_type: '',
    full_name: '',
    ...country === 'jp' && {
      full_name_kana: ''
    },
    email: '',
    name_dental_practice: '',
    country_code: '',
    dial_code: '',
    phone_number: '',
    years_dental_experience: '',
    clear_aligner_cases_completed: '',
    clinic_capacity: '',
    street_address: '',
    city: '',
    country: ''
  };

  const [formData, setFormData] = useState(defaultFormData);
  const [formErrors, setFormErrors] = useState(defaultFormErrors);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(false);

  useEffect(() => {
    const buffData = { ...formData };
    buffData.country_code = country;
    buffData.dial_code = countryData[country].dialCode;
    buffData.country = countryData[country].name;
    setFormData(buffData);
  }, [country]);

  const changeFormValue = (value) => {
    const buffData = { ...formData };
    Object.keys(value).forEach((key) => {
      buffData[key] = value[key];
    });
    setFormData(buffData);
  };

  const checkFormValues = () => {
    let flag = true;
    setFormError(null);
    const buffFormErrors = { ...formErrors };
    Object.keys(formErrors).forEach((key) => {
      if (formData[key] === '' && !(key === 'partner_type' && country === 'tw')) {
        buffFormErrors[key] = pageData.required;
        setFormError(pageData.correct_errors);
        flag = false;
      } else {
        buffFormErrors[key] = '';
      }
      if (key === 'email' && !validateEmail(formData[key])) {
        buffFormErrors[key] = loginData.invalid_email;
        setFormError(pageData.correct_errors);
        flag = false;
      }
    });
    setFormErrors(buffFormErrors);
    return flag;
  };

  const options = {
    headers: {
      'Content-type': 'application/x-www-form-urlencoded;charset=utf-8'
    }
  };

  const onFormSubmit = (e) => {
    e.preventDefault();
    if (checkFormValues()) {
      axios.post('https://hooks.zapier.com/hooks/catch/3376813/oy6bm9s/',
        qs.stringify(formData), options)
        .then(() => {
          setFormSuccess(true);
        })
        .catch(() => {
          setFormError('Error submiting the details please try again.');
        });
      clinicFormSubmissionEvent(formData);
      return true;
    }
    return false;
  };

  return (
    <div className="dentist-form-wrapper">
      <h2>{pageData.heading}</h2>
      <div className={`submit-success ${formSuccess ? 'show' : ''}`}>
        <h3>{pageData.thank_you}</h3>

        <p>{pageData.thank_two}</p>
      </div>
      <div className={`form-wrapper ${formSuccess ? 'hide' : ''}`}>
        {country !== 'tw' && (
        <div className="form-field">
          <Row>
            <Col xs="12">
              <h3>{pageData.i_am_a}</h3>
            </Col>
            <Col xs="6">
              <label
                className={`radioType ${formData.partner_type === 'Orthodontist' ? 'active' : ''}`}
                htmlFor="Orthodontist"
              >
                {pageData.orthodontist}
                <input
                  id="Orthodontist"
                  name="iam"
                  onChange={() => { changeFormValue({ partner_type: 'Orthodontist' }); }}
                  value="Orthodontist"
                  type="radio"
                  checked={formData.partner_type === 'Orthodontist'}
                />
              </label>
            </Col>
            <Col xs="6">
              <label htmlFor="Dentist" className={`radioType ${formData.partner_type === 'Dentist' ? 'active' : ''}`}>
                {pageData.dentist}
                <input
                  id="Dentist"
                  name="iam"
                  onChange={() => { changeFormValue({ partner_type: 'Dentist' }); }}
                  value="Dentist"
                  type="radio"
                  checked={formData.partner_type === 'Dentist'}
                />
              </label>
            </Col>
          </Row>
          <span className="form-error">{formErrors.partner_type}</span>
        </div>)}
        <div className="form-field">
          <Row>
            <Col xs="12">
              <h3>{pageData.details}</h3>
            </Col>
            <Col xs="12">
              <div className="form-item">
                <input
                  type="text"
                  value={formData.full_name}
                  className="input-default"
                  onChange={(e) => changeFormValue({ full_name: e.target.value })}
                  placeholder={pageData.full_name}
                />
                <span className="form-error">{formErrors.full_name}</span>
              </div>
            </Col>
            {country === 'jp' && (
              <Col xs="12">
                <div className="form-item">
                  <input
                    type="text"
                    value={formData.full_name_kana}
                    className="input-default"
                    onChange={(e) => changeFormValue({ full_name_kana: e.target.value })}
                    placeholder={pageData.full_name_kana}
                  />
                  <span className="form-error">{formErrors.full_name_kana}</span>
                </div>
              </Col>
            )}
            <Col xs="12">
              <div className="form-item">
                <input
                  type="text"
                  value={formData.name_dental_practice}
                  className="input-default"
                  placeholder={pageData.name_company}
                  onChange={(e) => changeFormValue({ name_dental_practice: e.target.value })}
                />
                <span className="form-error">{formErrors.name_dental_practice}</span>
              </div>
            </Col>
            <Col xs="12">
              <div className="form-item">
                <input
                  type="email"
                  value={formData.email}
                  className="input-default"
                  placeholder={loginData.email}
                  onChange={(e) => changeFormValue({ email: e.target.value })}
                />
                <span className="form-error">{formErrors.email}</span>
              </div>
            </Col>
            <Col xs="12">

              <div className="form-item">
                <Row>
                  <Col sm="3" md="2" xs="4">
                    <div
                      className="input-default btn text-left"
                      onClick={() => setIsOpen(!isOpen)}
                      role="presentation"
                      ref={dropdown}
                    >
                      <div className="flag-box">
                        <div className={`iti-flag ${getCountryCodeByIsd(formData.dial_code)}`} />
                      </div>
                      <img src="/img/icon_formarrow.svg" alt="form arrow" />
                      <ul className={`country-list ${isOpen ? 'show' : 'hide'}`} id="country-listbox">
                        {countryData && Object.keys(countryData).map((key) => (
                          <li
                            className="country standard"
                            role="presentation"
                            // eslint-disable-next-line max-len
                            onClick={() => { changeFormValue({ country_code: key, dial_code: countryData[key].dialCode }); }}
                          >
                            <div className="flag-box"><div className={`iti-flag ${key}`} /></div>
                            <span className="country-name">{countryData[key].name}</span>
                            <span className="dial-code">{countryData[key].dialCode}</span>
                          </li>
                        ))}

                      </ul>
                    </div>
                  </Col>
                  <Col xs="8" md="10" sm="9">
                    <input
                      type="number"
                      className="input-default"
                      placeholder={pageData.phone}
                      value={formData.phone_number}
                      onChange={(e) => changeFormValue({ phone_number: e.target.value })}
                    />
                    <span className="form-error">{formErrors.phone_number}</span>
                  </Col>

                </Row>

              </div>
            </Col>
            <Col xs="12">
              <div className="form-item">
                <select
                  className="input-default"
                  value={formData.years_dental_experience !== '' ? formData.years_dental_experience : pageData.exp}
                  defaultValue={pageData.exp}
                  onChange={(e) => changeFormValue({ years_dental_experience: e.target.value })}
                >
                  <option selected={true} disabled={true}>{pageData.exp}</option>
                  {Array.from(Array(100), (e, i) => <option value={i}>{i}</option>)}
                </select>
                <img src="/img/icon_formarrow.svg" alt="form arrow" />
                <span className="form-error">{formErrors.years_dental_experience}</span>
              </div>
            </Col>
            <Col xs="12">
              <div className="form-item">
                <select
                  className="input-default"
                  value={formData.clear_aligner_cases_completed !== '' ? formData.clear_aligner_cases_completed : pageData.cases}
                  defaultValue={pageData.cases}
                  onChange={(e) => changeFormValue({ clear_aligner_cases_completed: e.target.value })}
                >
                  <option selected={true} disabled={true}>{pageData.cases}</option>
                  <option value="Less than 10">{pageData.less_than_10}</option>
                  <option value="10 - 50">10 - 50</option>
                  <option value="50 - 100">50 - 100</option>
                  <option value="100+">100+</option>
                </select>
                <img src="/img/icon_formarrow.svg" alt="form arrow" />
                <span className="form-error">{formErrors.clear_aligner_cases_completed}</span>
              </div>
            </Col>
            <Col xs="12">
              <div className="form-item">
                <select
                  className="input-default"
                  value={formData.clinic_capacity !== '' ? formData.clinic_capacity : pageData.clinic_capacity}
                  defaultValue={pageData.clinic_capacity}
                  onChange={(e) => changeFormValue({ clinic_capacity: e.target.value })}
                >
                  <option selected={true} disabled={true}>{pageData.clinic_capacity}</option>
                  <option>1</option>
                  <option>2</option>
                  <option>3</option>
                  <option>4 +</option>
                </select>
                <img src="/img/icon_formarrow.svg" alt="form arrow" />
                <span className="form-error">{formErrors.clinic_capacity}</span>
              </div>
            </Col>
            <Col xs="12">
              <div className="form-item">
                <input
                  type="text"
                  className="input-default"
                  placeholder={pageData.street_address}
                  value={formData.street_address}
                  onChange={(e) => changeFormValue({ street_address: e.target.value })}
                />
                <span className="form-error">{formErrors.street_address}</span>
              </div>
            </Col>
            <Col xs="12">
              <div className="form-item">
                <input
                  type="text"
                  className="input-default"
                  placeholder={pageData.city}
                  value={formData.city}
                  onChange={(e) => changeFormValue({ city: e.target.value })}
                />
                <span className="form-error">{formErrors.city}</span>
              </div>
            </Col>
            <Col xs="12">
              <div className="form-item">
                <select
                  value={formData.country}
                  onChange={(e) => changeFormValue({ country: e.target.value })}
                  className="input-default"
                >
                  <option selected={true} disabled={true}>{pageData.country}</option>
                  {countryData && Object.values(countryData).map((val) => (
                    <option value={val.name}>{val.name}</option>
                  ))}
                </select>
                <img src="/img/icon_formarrow.svg" alt="form arrow" />
                <span className="form-error">{formErrors.country}</span>
              </div>
            </Col>
          </Row>
        </div>
        <div className="button-field">
          <div>
            <span>{formError}</span>
          </div>
          <a href="" onClick={onFormSubmit} className="btn web-btn web-btn--fill mx-auto">{pageData.submit}</a>
        </div>
      </div>
    </div>
  );
}

export default DentistForm;
